import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Info, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Switch } from '@/components/ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

import { supabase } from '@/integrations/supabase/client';
import { PATIENT_INACTIVE_TOAST_MESSAGE } from '@/lib/patient-status-errors';

interface CareConfigDelegationToggleProps {
  memberId: string;
  caregiverName: string;
  enabled: boolean;
  canGrant: boolean;
  isPatientActive: boolean;
  onUpdate?: () => void;
}

/**
 * Lets an owner (or company manager/coordinator) delegate care plan and
 * schedule management to a specific caregiver.
 */
export function CareConfigDelegationToggle({
  memberId,
  caregiverName,
  enabled,
  canGrant,
  isPatientActive,
  onUpdate,
}: CareConfigDelegationToggleProps) {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [optimistic, setOptimistic] = useState<boolean | null>(null);

  const isOn = optimistic ?? enabled;

  if (!canGrant) {
    if (!isOn) return null;
    return (
      <div className="mt-1.5 inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">
        <ShieldCheck className="h-3 w-3" />
        {t('team.delegation.badge')}
      </div>
    );
  }

  const handleToggle = async (next: boolean) => {
    if (!isPatientActive) {
      toast.error(PATIENT_INACTIVE_TOAST_MESSAGE);
      return;
    }
    setSaving(true);
    setOptimistic(next);
    const { error } = await supabase
      .from('patient_user_links')
      .update({ can_manage_care_config: next } as any)
      .eq('id', memberId);
    setSaving(false);

    if (error) {
      setOptimistic(null);
      toast.error(t('team.delegation.error'));
      return;
    }

    toast.success(
      next
        ? t('team.delegation.granted', { name: caregiverName })
        : t('team.delegation.revoked', { name: caregiverName })
    );
    onUpdate?.();
  };

  return (
    <div className="mt-2 rounded-lg border border-border bg-muted/40 px-2.5 py-2">
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-primary" />
        <p className="flex-1 min-w-0 truncate text-xs font-medium">
          {t('team.delegation.title')}
        </p>
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="shrink-0 rounded-full p-1 text-muted-foreground hover:text-foreground"
              aria-label={t('team.delegation.title')}
            >
              <Info className="h-3.5 w-3.5" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-72 space-y-2 text-xs">
            <p className="font-medium">{t('team.delegation.title')}</p>
            <p className="text-muted-foreground">{t('team.delegation.description')}</p>
            <p className="text-muted-foreground">{t('team.delegation.limits')}</p>
          </PopoverContent>
        </Popover>
        <Switch
          checked={isOn}
          disabled={saving}
          onCheckedChange={handleToggle}
          aria-label={t('team.delegation.title')}
        />
      </div>
    </div>
  );
}

